import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Routes, Link, useLocation, useNavigate } from 'react-router-dom';
import { Mountain, Grid, List, Map as MapIcon, Search, Bookmark, ArrowUpCircle, Download, Menu, X, Trophy } from 'lucide-react';
import ViewCard from './components/ViewCard';
import { ViewModal } from './components/ViewModal';
import { ViewModalMobile } from './components/ViewModalMobile';
import { useIsMobile } from './hooks/useIsMobile';
import type { View } from './types';
import { ToastProvider, ToastViewport } from './components/ui/toast';
import { useTheme } from './lib/ThemeContext';
import { fetchViews } from './lib/api';
import { FilterBar, FilterState } from './components/FilterBar';
import { cn } from './lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from './hooks/useMediaQuery';
import { ViewSkeleton } from './components/ViewSkeleton';
import { ShareDialog } from './components/ShareDialog';
import { Pagination } from './components/Pagination';
import { usePaginationState } from './hooks/usePaginationState';
import { SectionHeader } from './components/SectionHeader';
import { Analytics } from '@vercel/analytics/react';
import { useToast } from './components/ui/toast';
import SpaceBackground from './components/SpaceBackground';
import { ViewDebugInfo } from './components/ViewDebugInfo';
import { AppRoutes } from './routes'; // Import the AppRoutes component
import { useSettings } from './hooks/useSettings';
import { Footer } from './components/Footer';
import { ResponsiveLatestConfirmedWidget } from './components/ResponsiveLatestConfirmedWidget';
import { ResponsiveSubredditWidget } from './components/ResponsiveSubredditWidget';
import { InteractiveMapLeaflet } from './components/map/InteractiveMapLeaflet';

// Import page components
import FAQPage from './pages/faq/page';
import SubmitViewPage from './pages/submit/page';
import ContactPage from './pages/contact/page';
import PrivacyPolicyPage from './pages/privacy/page';
import TermsAndConditionsPage from './pages/terms/page';
import RecordsPage from './pages/records/page';

// Import SEO components
import { HomePageSEO } from './pages/home/SEO';
import { FAQPageSEO } from './pages/faq/SEO';
import { ContactPageSEO } from './pages/contact/SEO';
import { RecordsPageSEO } from './pages/records/SEO';

// Define types for the layout components
interface ViewsLayoutProps {
  views: View[];
  paginatedGroupedViews: {
    range: string;
    views: View[];
  }[];
  isLoading: boolean;
  error: string | null;
  viewMode: 'grid' | 'list' | 'map';
  setViewMode: (mode: 'grid' | 'list' | 'map') => void;
  isCompactMode: boolean;
  setIsCompactMode: (isCompact: boolean) => void;
  handleFilter: (filters: FilterState) => void;
  setSelectedView: (view: View | null) => void;
  toggleBookmark: (viewId: string) => void;
  isBookmarked: (viewId: string) => boolean;
  handleShare: (view: View) => void;
  viewTransition: 'fade' | 'slide';
  setViewTransition: (transition: 'fade' | 'slide') => void;
  currentPage: number;
  totalPages: number;
  perPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  isFilterApplied: boolean;
  onRandomView: () => void;
}

interface BookmarksLayoutProps {
  bookmarkedViews: View[];
  paginatedGroupedViews: {
    range: string;
    views: View[];
  }[];
  isLoading: boolean;
  error: string | null;
  viewMode: 'grid' | 'list' | 'map';
  setViewMode: (mode: 'grid' | 'list' | 'map') => void;
  isCompactMode: boolean;
  setIsCompactMode: (isCompact: boolean) => void;
  handleFilter: (filters: FilterState) => void;
  setSelectedView: (view: View | null) => void;
  toggleBookmark: (viewId: string) => void;
  isBookmarked: (viewId: string) => boolean;
  handleShare: (view: View) => void;
  exportData: () => void;
  viewTransition: 'fade' | 'slide';
  setViewTransition: (transition: 'fade' | 'slide') => void;
  currentPage: number;
  totalPages: number;
  perPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
}

// Replace the existing getIncrementingVersion function with a more comprehensive version
function getIncrementingVersion() {
  // Get version from package.json
  const packageVersion = "0.0.0"; // From package.json
  
  // Try to get the build number and timestamp from localStorage
  const storedBuildInfo = localStorage.getItem('tvs-build-info');
  let buildInfo = { number: 1, deployments: 0, lastUpdate: new Date().toISOString() };
  
  if (storedBuildInfo) {
    try {
      const parsedInfo = JSON.parse(storedBuildInfo);
      // Increment build number on each refresh
      buildInfo = {
        number: parsedInfo.number + 1,
        // Only increment deployments once per session
        deployments: parsedInfo.deployments,
        lastUpdate: parsedInfo.lastUpdate
      };
    } catch (e) {
      console.error('Failed to parse build info', e);
    }
  }
  
  // Check if this is a new session/deployment
  const lastSessionDate = sessionStorage.getItem('tvs-last-session');
  const currentDate = new Date().toISOString().split('T')[0]; // Get just the date part
  
  if (!lastSessionDate || lastSessionDate !== currentDate) {
    // This is a new session/deployment or a new day
    buildInfo.deployments += 1;
    buildInfo.lastUpdate = new Date().toISOString();
    sessionStorage.setItem('tvs-last-session', currentDate);
  }
  
  // Store the updated build info
  localStorage.setItem('tvs-build-info', JSON.stringify(buildInfo));
  
  // Calculate days since last update
  const daysSinceUpdate = Math.floor(
    (new Date().getTime() - new Date(buildInfo.lastUpdate).getTime()) / (1000 * 60 * 60 * 24)
  );
  
  // Return formatted version string
  return {
    version: `${packageVersion}-build.${buildInfo.number}`,
    buildNumber: buildInfo.number,
    deployments: buildInfo.deployments,
    lastUpdate: buildInfo.lastUpdate,
    daysSinceUpdate
  };
}

function App() {
  const { theme } = useTheme();
  const location = useLocation(); // Get current location
  const isMobileDevice = useIsMobile();
  
  // NAVIGATION FIX: Force re-renders when location changes
  const [currentRoute, setCurrentRoute] = useState(location.pathname);
  const [renderKey, setRenderKey] = useState(0);
  
  useEffect(() => {
    console.log(`🚀 Navigation: ${location.pathname}`);
    setCurrentRoute(location.pathname);
    setRenderKey(prev => prev + 1); // Force re-render with new key
  }, [location.pathname]);
  
  const { settings } = useSettings();
  const [viewMode, setViewMode] = useState<'grid' | 'list' | 'map'>('grid');
  const [selectedView, setSelectedView] = useState<View | null>(null);
  const [views, setViews] = useState<View[]>([]);
  const [filteredViews, setFilteredViews] = useState<View[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [shareView, setShareView] = useState<View | null>(null);
  const [bookmarkedViews, setBookmarkedViews] = useState<string[]>(() => {
    const saved = localStorage.getItem('bookmarkedViews');
    return saved ? JSON.parse(saved) : [];
  });
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [viewTransition, setViewTransition] = useState<'fade' | 'slide'>('fade');
  const [isCompactMode, setIsCompactMode] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Add a ref to track if views have been loaded to prevent multiple loads
  const viewsLoadedRef = useRef(false);
  
  // Background configuration - optimized for all users with higher opacity
  const [backgroundConfig, setBackgroundConfig] = useState({
    show: true,
    opacity: 0.9, // Higher opacity for better visibility
    interval: 120000, // 2 minutes - more frequent cycling while maintaining performance
    selectedBackground: undefined as string | undefined
  });
  
  // Parse initial filters from URL parameters
  const parseInitialFiltersFromURL = (): FilterState => {
    const params = new URLSearchParams(location.search);
    const urlFilters: Partial<FilterState> = {};
    
    // Search parameter
    const search = params.get('search');
    if (search) urlFilters.search = search;
    
    // Status parameters (comma-separated)
    const status = params.get('status');
    if (status) {
      urlFilters.status = status.split(',');
    } else {
      urlFilters.status = ['confirmed']; // Default to confirmed
    }
    
    // Distance parameter
    const distance = params.get('distance');
    if (distance) {
      urlFilters.distance = distance;
    } else {
      urlFilters.distance = "1000";
    }
    
    // Source location parameters
    const sourceStates = params.get('sourceStates');
    if (sourceStates) {
      urlFilters.sourceStates = sourceStates.split(',');
    } else {
      urlFilters.sourceStates = [];
    }
    
    const sourceCountries = params.get('sourceCountries');
    if (sourceCountries) {
      urlFilters.sourceCountries = sourceCountries.split(',');
    } else {
      urlFilters.sourceCountries = [];
    }
    
    const sourceContinent = params.get('sourceContinent');
    if (sourceContinent) {
      urlFilters.sourceContinent = sourceContinent.split(',');
    } else {
      urlFilters.sourceContinent = [];
    }
    
    // Destination location parameters
    const destStates = params.get('destStates');
    if (destStates) {
      urlFilters.destinationStates = destStates.split(',');
    } else {
      urlFilters.destinationStates = [];
    }
    
    const destCountries = params.get('destCountries');
    if (destCountries) {
      urlFilters.destinationCountries = destCountries.split(',');
    } else {
      urlFilters.destinationCountries = [];
    }
    
    const destContinent = params.get('destContinent');
    if (destContinent) {
      urlFilters.destinationContinent = destContinent.split(',');
    } else {
      urlFilters.destinationContinent = [];
    }
    
    // Type parameter
    const type = params.get('type');
    if (type) {
      urlFilters.type = type.split(',');
    } else {
      urlFilters.type = [];
    }
    
    // Sort parameters
    const sortBy = params.get('sortBy');
    if (sortBy) {
      urlFilters.sortBy = sortBy;
    } else {
      urlFilters.sortBy = 'distance';
    }
    
    const sortDirection = params.get('sortDirection');
    if (sortDirection === 'asc' || sortDirection === 'desc') {
      urlFilters.sortDirection = sortDirection;
    } else {
      urlFilters.sortDirection = 'desc';
    }
    
    // Merge with defaults
    return {
      search: urlFilters.search || null,
      status: urlFilters.status || ['confirmed'],
      sourceStates: urlFilters.sourceStates || [],
      sourceCountries: urlFilters.sourceCountries || [],
      sourceContinent: urlFilters.sourceContinent || [],
      destinationStates: urlFilters.destinationStates || [],
      destinationCountries: urlFilters.destinationCountries || [],
      destinationContinent: urlFilters.destinationContinent || [],
      distance: urlFilters.distance || "1000",
      sortBy: urlFilters.sortBy || 'distance',
      sortDirection: urlFilters.sortDirection || 'desc',
      category: [],
      seasons: [],
      type: urlFilters.type || [],
      location: '',
      exactTypeMatch: true
    };
  };
  
  // 1. Move filters state to App component - initialized from URL
  const [filters, setFilters] = useState<FilterState>(() => parseInitialFiltersFromURL());
  
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 768px)');
  
  // Define isBookmarked function before using it
  const isBookmarked = (viewId: string) => bookmarkedViews.includes(viewId);
  
  // Get bookmarked views
  const filteredBookmarkedViews = filteredViews.filter(view => isBookmarked(view.id));
  
  // Use the pagination state hook
  const {
    currentPage,
    perPage,
    totalPages,
    totalViews,
    paginatedViews,
    handlePageChange,
    handlePerPageChange,
    bookmarkCurrentPage,
    bookmarkPerPage,
    totalBookmarkPages,
    totalBookmarkViews,
    paginatedBookmarkedViews,
    handleBookmarkPageChange,
    handleBookmarkPerPageChange,
  } = usePaginationState(filteredViews, filteredBookmarkedViews);
  
  // Update version state to include more information
  const [buildVersion, setBuildVersion] = useState<{
    version: string;
    buildNumber: number;
    deployments: number;
    lastUpdate: string;
    daysSinceUpdate: number;
  } | null>(null);
  
  // Initialize version on mount
  useEffect(() => {
    setBuildVersion(getIncrementingVersion());
  }, []);
  
  // List of routes that don't need to load all views data
  const nonDataRoutes = ['/submit', '/contact', '/privacy', '/terms', '/faq'];
  
  // Check if current route needs view data
  const shouldLoadViews = !nonDataRoutes.includes(location.pathname);
  
  // Create a stable reference to initialFilterState to avoid dependency issues
  const initialFilterState = useMemo(() => ({
    search: null,
    status: ['confirmed'],
    sourceStates: [],
    sourceCountries: [],
    sourceContinent: [],
    destinationStates: [],
    destinationCountries: [],
    destinationContinent: [],
    distance: "1000",
    sortBy: 'distance',
    sortDirection: 'desc' as const,
    category: [],
    seasons: [],
    type: [],
    location: '',
    exactTypeMatch: true
  }), []); // Empty dependency array - this should never change
  
  // Fetch views data (runs once per route that needs it)
  useEffect(() => {
    if (!shouldLoadViews) {
      setIsLoading(false);
      return;
    }

    if (viewsLoadedRef.current) {
      return;
    }

    const loadViews = async () => {
      try {
        setIsLoading(true);
        viewsLoadedRef.current = true;
        const data = await fetchViews();
        setViews(data);
        setError(null);
      } catch (err) {
        setError('Failed to load views. Please try again later.');
        viewsLoadedRef.current = false;
      } finally {
        setIsLoading(false);
      }
    };

    loadViews();
  }, [shouldLoadViews]);

  // Apply filters to loaded views (runs when views load or filters change)
  useEffect(() => {
    if (views.length === 0) return;

    const filtered = views.filter(view => {
      const statuses = filters.status || ['confirmed'];
      const matchesStatus = statuses.some(status => view.status === status);

      let matchesDistance = true;
      if (filters.distance) {
        const distance = view.distanceKm;
        const maxDistance = Number.parseFloat(filters.distance);
        matchesDistance = !Number.isNaN(distance) && !Number.isNaN(maxDistance) && distance <= maxDistance;
      }

      let matchesSearch = true;
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        const searchableView = {
          source: view.source,
          destination: view.destination,
          state: view.state,
          country: view.country,
          continent: view.continent,
          destinationState: view.destinationState,
          destinationCountry: view.destinationCountry,
          destinationContinent: view.destinationContinent,
          type: view.type,
          distance: view.distance,
          status: view.status
        };
        const viewString = JSON.stringify(searchableView).toLowerCase();
        matchesSearch = viewString.includes(searchTerm);
      }

      let matchesType = true;
      if (filters.type && filters.type.length > 0) {
        const typesToCheck = filters.type.map(t => t.toLowerCase());
        const viewType = view.type;
        if (typeof viewType === 'string') {
          matchesType = typesToCheck.includes(viewType.toLowerCase());
        } else if (Array.isArray(viewType)) {
          matchesType = viewType.some(t => typesToCheck.includes(t.toLowerCase()));
        } else {
          matchesType = false;
        }
      }

      let matchesSourceLocation = true;
      if (filters.sourceStates.length > 0) {
        matchesSourceLocation = filters.sourceStates.includes(view.state || '');
      }
      if (matchesSourceLocation && filters.sourceCountries.length > 0) {
        matchesSourceLocation = filters.sourceCountries.includes(view.country || '');
      }
      if (matchesSourceLocation && filters.sourceContinent.length > 0) {
        matchesSourceLocation = filters.sourceContinent.includes(view.continent || '');
      }

      let matchesDestLocation = true;
      if (filters.destinationStates.length > 0) {
        matchesDestLocation = filters.destinationStates.includes(view.destinationState || '');
      }
      if (matchesDestLocation && filters.destinationCountries.length > 0) {
        matchesDestLocation = filters.destinationCountries.includes(view.destinationCountry || '');
      }
      if (matchesDestLocation && filters.destinationContinent.length > 0) {
        matchesDestLocation = filters.destinationContinent.includes(view.destinationContinent || '');
      }

      return matchesStatus && matchesDistance && matchesSearch && matchesType && matchesSourceLocation && matchesDestLocation;
    });

    const sorted = [...filtered].sort((a, b) => {
      if (filters.sortBy === 'distance') {
        const distanceA = a.distanceKm;
        const distanceB = b.distanceKm;
        return filters.sortDirection === 'asc' ? distanceA - distanceB : distanceB - distanceA;
      }
      return 0;
    });

    setFilteredViews(sorted);
    setIsFilterApplied(filters.search !== null || filters.type.length > 0 ||
                        filters.sourceStates.length > 0 || filters.sourceCountries.length > 0 ||
                        filters.destinationStates.length > 0 || filters.destinationCountries.length > 0);
  }, [views, filters]);

  // Handle URL parameters for direct linking
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewId = params.get('view');
    
    if (viewId) {
      const view = views.find(v => v.id === viewId);
      if (view) {
        setSelectedView(view);
      }
    }
  }, [location.search, views]);
  
  // Save bookmarks to localStorage
  useEffect(() => {
    localStorage.setItem('bookmarkedViews', JSON.stringify(bookmarkedViews));
  }, [bookmarkedViews]);
  
  // Use the existing handleScroll function but with throttling for better performance
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollToTop(window.scrollY > 500);
    };
    
    // Add a throttle function to limit how often the scroll handler runs
    let isScrolling = false;
    const throttledScroll = () => {
      if (!isScrolling) {
        isScrolling = true;
        window.requestAnimationFrame(() => {
          handleScroll();
          isScrolling = false;
        });
      }
    };
    
    window.addEventListener('scroll', throttledScroll);
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  const handleFilter = (filters: FilterState) => {
    // Handle filter called
    
    // If no filters are applied, return all views
    if (
      (!filters.search || filters.search.length === 0) &&
      (!filters.type || filters.type.length === 0) &&
      (!filters.status || filters.status.length === 0) &&
      filters.sourceStates.length === 0 &&
      filters.sourceCountries.length === 0 &&
      filters.sourceContinent.length === 0 &&
      filters.destinationStates.length === 0 &&
      filters.destinationCountries.length === 0 &&
      filters.destinationContinent.length === 0 &&
      (filters.distance === "50" || !filters.distance) // Default max distance
    ) {
      // No filters applied, setting filteredViews to all views
      setFilteredViews(views);
      return;
    }

    // Track if any filter was applied
    setIsFilterApplied(true);

    // Use requestAnimationFrame to prevent blocking the UI during filtering
    requestAnimationFrame(() => {
      // Create a new filtered list
      const newFilteredViews = views.filter((view) => {
        let matched = true;

        // Check type filter
        if (filters.type && filters.type.length > 0) {
          const typesToCheck = filters.type.map((t) => t.toLowerCase());
          const viewType = view.type;
          
          // Try exact matches first
          if (filters.exactTypeMatch) {
            // For string type
            if (typeof viewType === 'string') {
              const normalizedType = viewType.toLowerCase();
              matched = typesToCheck.includes(normalizedType);
            } 
            // For array of types
            else if (Array.isArray(viewType)) {
              matched = viewType.some(t => {
                if (typeof t === 'string') {
                  return typesToCheck.includes(t.toLowerCase());
                }
                return false;
              });
            }
            // No valid type
            else {
              matched = false;
            }
          } 
          // Partial matching
          else {
            // Convert the entire view to a string and check if it contains any of the types
            const viewString = JSON.stringify(view).toLowerCase();
            matched = typesToCheck.some((type) => viewString.includes(type));
          }

          // If no match on type, skip other filters
          if (!matched) return false;
        }

        // Check source location filters - Check continents first
        if (matched && filters.sourceContinent && filters.sourceContinent.length > 0) {
          matched = Boolean(view.continent && filters.sourceContinent.includes(normalizeContinent(view.continent)));
          if (!matched) return false;
        }

        // Check source country filter
        if (matched && filters.sourceCountries && filters.sourceCountries.length > 0) {
          matched = Boolean(view.country && filters.sourceCountries.includes(view.country));
          if (!matched) return false;
        }

        // Check source state filter
        if (matched && filters.sourceStates && filters.sourceStates.length > 0) {
          matched = Boolean(view.state && filters.sourceStates.includes(view.state));
          if (!matched) return false;
        }

        // Check destination location filters - Check continents first
        if (matched && filters.destinationContinent && filters.destinationContinent.length > 0) {
          matched = Boolean(view.destinationContinent && filters.destinationContinent.includes(normalizeContinent(view.destinationContinent)));
          if (!matched) return false;
        }

        // Check destination country filter
        if (matched && filters.destinationCountries && filters.destinationCountries.length > 0) {
          matched = Boolean(view.destinationCountry && filters.destinationCountries.includes(view.destinationCountry));
          if (!matched) return false;
        }

        // Check destination state filter
        if (matched && filters.destinationStates && filters.destinationStates.length > 0) {
          matched = Boolean(view.destinationState && filters.destinationStates.includes(view.destinationState));
          if (!matched) return false;
        }

        // Check distance filter
        if (matched && filters.distance) {
          const filterDistance = 
            typeof filters.distance === 'number' 
              ? filters.distance
              : Number.parseFloat(filters.distance);
          
          if (!Number.isNaN(filterDistance)) {
            const viewDistance = view.distanceKm;
            
            matched = !Number.isNaN(viewDistance) && viewDistance <= filterDistance;
            if (!matched) return false;
          }
        }

        // Check status filter
        if (matched && filters.status && filters.status.length > 0) {
          // Status can be a string or an array of strings
          const statusToCheck = filters.status.map((s) => s.toLowerCase());
          const viewStatus = view.status;

          if (typeof viewStatus === 'string') {
            matched = statusToCheck.includes(viewStatus.toLowerCase());
          } else if (Array.isArray(viewStatus)) {
            // This branch should never execute since status is defined as string in View interface
            matched = (viewStatus as string[]).some(s => statusToCheck.includes(s.toLowerCase()));
          } else {
            matched = false;
          }
          
          if (!matched) return false;
        }

        // Check search filter - optimized for smoother performance
        if (matched && filters.search && filters.search.length > 0) {
          const searchTerm = filters.search.toLowerCase();
          // Create a simplified view object for search to focus on key fields
          const searchableView = {
            source: view.source,
            destination: view.destination,
            state: view.state,
            country: view.country,
            continent: view.continent,
            destinationState: view.destinationState,
            destinationCountry: view.destinationCountry,
            destinationContinent: view.destinationContinent,
            type: view.type,
            distance: view.distance,
            status: view.status
          };
          const viewString = JSON.stringify(searchableView).toLowerCase();
          matched = viewString.includes(searchTerm);
          if (!matched) return false;
        }

        return matched;
      });

      // Apply sorting to the filtered views
      const sortedViews = [...newFilteredViews].sort((a, b) => {
        if (filters.sortBy === 'distance') {
          const distanceA = a.distanceKm;
          const distanceB = b.distanceKm;
          return filters.sortDirection === 'asc' ? distanceA - distanceB : distanceB - distanceA;
        }
        // Add other sorting options here as needed
        return 0;
      });
      
      // Update filtered views in a single batch to prevent multiple re-renders
      setFilteredViews(sortedViews);
    });
  };

  const distanceRanges = ['500+km', '100-500km', '10-100km'];

  // Use memoized calculation for grouped views to optimize performance
  const paginatedGroupedViews = useMemo(() => {
    return distanceRanges.map(range => {
    let min = 0;
    let max = Number.POSITIVE_INFINITY;
    
    if (range === '500+km') {
      min = 500;
    } else if (range === '100-500km') {
      min = 100;
      max = 500;
    } else if (range === '10-100km') {
      min = 10;
      max = 100;
    }
    
      // Filter the paginated views by distance range
    return {
      range,
      views: paginatedViews.filter(view => {
          const distance = view.distanceKm;
          
          // Check if distance is within the range
          return !isNaN(distance) && distance >= min && distance < max;
      })
    };
    }).filter(group => group.views.length > 0); // Only show categories that have views
  }, [paginatedViews, distanceRanges]);

  // Use memoized calculation for bookmarked grouped views
  const bookmarkPaginatedGroupedViews = useMemo(() => {
    return distanceRanges.map(range => {
    let min = 0;
    let max = Number.POSITIVE_INFINITY;
    
    if (range === '500+km') {
      min = 500;
    } else if (range === '100-500km') {
      min = 100;
      max = 500;
    } else if (range === '10-100km') {
      min = 10;
      max = 100;
    }
    
      // Filter the paginated bookmarked views by distance range
    return {
      range,
      views: paginatedBookmarkedViews.filter(view => {
          const distance = view.distanceKm;
          
          // Check if distance is within the range
          return !isNaN(distance) && distance >= min && distance < max;
      })
    };
    }).filter(group => group.views.length > 0); // Only show categories that have views
  }, [paginatedBookmarkedViews, distanceRanges]);

  const toggleBookmark = (viewId: string) => {
    setBookmarkedViews(prev => 
      prev.includes(viewId)
        ? prev.filter(id => id !== viewId)
        : [...prev, viewId]
    );
  };
  
  const handleShare = (view: View) => {
    const url = `${window.location.origin}/?view=${view.id}`;
    setShareUrl(url);
    setShareView(view);
    setIsShareDialogOpen(true);
  };
  
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const exportData = () => {
    const bookmarkedViewsData = views.filter(view => isBookmarked(view.id));
    const dataStr = JSON.stringify(bookmarkedViewsData, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
    
    const exportFileDefaultName = `viewshed-bookmarks-${new Date().toISOString().slice(0, 10)}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  // Add a function to update notes for a view
  const updateViewNotes = useCallback((viewId: string, notes: string) => {
    // Update the selected view if it's currently displayed
    if (selectedView && selectedView.id === viewId) {
      setSelectedView(prev => prev ? { ...prev, notes } : null);
    }
    
    // Also update the view in the views array for persistence
    setViews(prev => prev.map(view => 
      view.id === viewId ? { ...view, notes } : view
    ));
    
    // Save to localStorage for persistence
    const updatedViews = views.map(view => 
      view.id === viewId ? { ...view, notes } : view
    );
    localStorage.setItem('viewsWithNotes', JSON.stringify(updatedViews));
  }, [selectedView, views]);
  
  // Load saved notes on initialization
  useEffect(() => {
    const savedViewsWithNotes = localStorage.getItem('viewsWithNotes');
    if (savedViewsWithNotes) {
      try {
        const parsedViews = JSON.parse(savedViewsWithNotes);
        
        // Check if it's a valid array
        if (Array.isArray(parsedViews) && parsedViews.length > 0) {
          // Merge saved notes with current views
          setViews(prevViews => prevViews.map(view => {
            const savedView = parsedViews.find(v => v.id === view.id);
            return savedView?.notes ? { ...view, notes: savedView.notes } : view;
          }));
        }
      } catch (e) {
        console.error('Failed to parse saved views with notes', e);
      }
    }
  }, []);

  // Handle browser back button to close modal on mobile
  useEffect(() => {
    // Only apply this on mobile devices
    if (!isMobileDevice) return;

    const handlePopState = (event: PopStateEvent) => {
      // If modal is open and we're going back, close the modal
      if (selectedView !== null) {
        event.preventDefault();
        setSelectedView(null);
      }
    };

    // When modal opens, push a state to history
    if (selectedView !== null) {
      // Only push if we don't already have a modal state
      if (!window.history.state?.modalOpen) {
        window.history.pushState({ modalOpen: true, viewId: selectedView.id }, '');
      }
    }

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedView, isMobileDevice]);

  // Normalize continent values for consistent naming
  const normalizeContinent = (continent: string): string => {
    // Map Oceania to Australasia to maintain consistency
    if (continent === 'Oceania') {
      return 'Australasia';
    }
    return continent;
  };

  // Add random view handler function
  const handleRandomView = () => {
    if (filteredViews.length > 0) {
      const randomIndex = Math.floor(Math.random() * filteredViews.length);
      const randomView = filteredViews[randomIndex];
      setSelectedView(randomView);
    }
  };

  // NAVIGATION FIX: Robust page rendering function
  const renderCurrentPage = () => {
    console.log(`🎯 Rendering page for route: ${currentRoute}`);
    
    switch (currentRoute) {
      case '/':
        return (
          <>
            <HomePageSEO />
            <ViewsLayout 
              key="home-layout"
              views={filteredViews}
              paginatedGroupedViews={paginatedGroupedViews}
              isLoading={isLoading}
              error={error}
              viewMode={viewMode}
              setViewMode={setViewMode}
              isCompactMode={isCompactMode}
              setIsCompactMode={setIsCompactMode}
              handleFilter={handleFilter}
              setSelectedView={setSelectedView}
              toggleBookmark={toggleBookmark}
              isBookmarked={isBookmarked}
              handleShare={handleShare}
              viewTransition={viewTransition}
              setViewTransition={setViewTransition}
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              totalItems={totalViews}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              filters={filters}
              setFilters={setFilters}
              isFilterApplied={isFilterApplied}
              onRandomView={handleRandomView}
            />
          </>
        );
        
      case '/bookmarks':
        return (
          <BookmarksLayout 
            key="bookmarks-layout"
            bookmarkedViews={filteredBookmarkedViews}
            paginatedGroupedViews={bookmarkPaginatedGroupedViews}
            isLoading={isLoading}
            error={error}
            viewMode={viewMode}
            setViewMode={setViewMode}
            isCompactMode={isCompactMode}
            setIsCompactMode={setIsCompactMode}
            handleFilter={handleFilter}
            setSelectedView={setSelectedView}
            toggleBookmark={toggleBookmark}
            isBookmarked={isBookmarked}
            handleShare={handleShare}
            exportData={exportData}
            viewTransition={viewTransition}
            setViewTransition={setViewTransition}
            currentPage={bookmarkCurrentPage}
            totalPages={totalBookmarkPages}
            perPage={bookmarkPerPage}
            totalItems={totalBookmarkViews}
            onPageChange={handleBookmarkPageChange}
            onPerPageChange={handleBookmarkPerPageChange}
            filters={filters}
            setFilters={setFilters}
          />
        );
        
      case '/faq':
        return (
          <>
            <FAQPageSEO />
            <FAQPage key="faq-page" />
          </>
        );

      case '/records':
        return (
          <>
            <RecordsPageSEO />
            <RecordsPage
              key="records-page"
              onViewClick={setSelectedView}
              onShare={handleShare}
            />
          </>
        );

      case '/submit':
        return <SubmitViewPage key="submit-page" />;
        
      case '/contact':
        return (
          <>
            <ContactPageSEO />
            <ContactPage key="contact-page" />
          </>
        );
        
      case '/privacy':
        return <PrivacyPolicyPage key="privacy-page" />;
        
      case '/terms':
        return <TermsAndConditionsPage key="terms-page" />;
        
      default:
        console.log(`⚠️ Unknown route: ${currentRoute}, defaulting to home`);
        return (
          <>
            <HomePageSEO />
            <ViewsLayout 
              key="default-layout"
              views={filteredViews}
              paginatedGroupedViews={paginatedGroupedViews}
              isLoading={isLoading}
              error={error}
              viewMode={viewMode}
              setViewMode={setViewMode}
              isCompactMode={isCompactMode}
              setIsCompactMode={setIsCompactMode}
              handleFilter={handleFilter}
              setSelectedView={setSelectedView}
              toggleBookmark={toggleBookmark}
              isBookmarked={isBookmarked}
              handleShare={handleShare}
              viewTransition={viewTransition}
              setViewTransition={setViewTransition}
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              totalItems={totalViews}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
              filters={filters}
              setFilters={setFilters}
              isFilterApplied={isFilterApplied}
              onRandomView={handleRandomView}
            />
          </>
        );
    }
  };

  return (
    <ToastProvider>
      <div className="min-h-screen dark">
        {/* Space background - now with optimized cycling */}
        {backgroundConfig.show && (
          <SpaceBackground 
            opacity={backgroundConfig.opacity} 
            interval={backgroundConfig.interval}
            selectedBackground={backgroundConfig.selectedBackground}
            disabled={!backgroundConfig.show}
          />
        )}
        
        <div className="min-h-screen bg-background/50">
          {/* Header */}
          <header className="bg-background/70 shadow-sm z-[7777] border-b border-border backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
              <div className="flex items-center h-32 relative">
                {/* Left Navigation */}
                <div className="flex items-center">
                  <nav className="hidden md:flex space-x-8">
                    <Link to="/" 
                      onClick={() => {
                        console.log('🔗 Navigation click: Home');
                        if (currentPage !== 1) {
                          handlePageChange(1);
                        }
                      }}
                      className={`transition-colors ${location.pathname === '/' ? 'text-primary font-medium' : 'text-foreground hover:text-primary'}`}>Home</Link>
                    <Link to="/submit" 
                      onClick={() => console.log('🔗 Navigation click: Submit View')}
                      className={`transition-colors ${location.pathname === '/submit' ? 'text-primary font-medium' : 'text-foreground hover:text-primary'}`}>Submit View</Link>
                  </nav>
                </div>

                {/* Centered Logo with Tagline - properly centered on all screen sizes */}
                <div className="pointer-events-none absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center pt-4 pb-4 w-full px-16 sm:px-4 sm:w-auto sm:max-w-[calc(100vw-120px)] md:max-w-none">
                  <Link
                    to="/"
                    className="pointer-events-auto flex flex-col items-center"
                    onClick={() => {
                      if (currentPage !== 1) {
                        handlePageChange(1);
                      }
                    }}
                  >
                    <div className="flex items-center justify-center">
                      <img src="/favicon.svg" alt="TheViewShed Logo" className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16" />
                      <h1 className="ml-2 md:ml-3 text-lg sm:text-xl md:text-2xl font-bold gradient-text whitespace-nowrap">
                        {settings?.siteName || 'TheViewShed'}
                      </h1>
                    </div>
                    <p className="text-[10px] sm:text-xs text-muted-foreground font-light mt-1 text-center">
                      Elevate your perspective
                    </p>
                    <p className="text-[10px] sm:text-xs md:text-base font-medium text-foreground/80 mt-1 italic text-center leading-tight">
                      The Global Index of Earth's Ultra-Long-Distance Sightlines
                    </p>
                  </Link>
                </div>

                {/* Right Navigation */}
                <div className="ml-auto flex items-center">
                  <nav className="hidden md:flex space-x-8">
                    <Link to="/records"
                      onClick={() => console.log('🔗 Navigation click: Records')}
                      className={`relative bottom-1 flex items-center gap-1.5 px-3 py-2 border rounded-md transition-all font-medium h-[38px] bg-gradient-to-r from-amber-100/80 to-yellow-200/50 dark:from-amber-900/30 dark:to-yellow-800/40 hover:from-amber-200/70 hover:to-yellow-300/50 dark:hover:from-amber-800/40 dark:hover:to-yellow-700/50 border-amber-300/70 dark:border-amber-600/50 hover:border-amber-400/80 dark:hover:border-amber-500/60 ${location.pathname === '/records' ? 'ring-2 ring-amber-400/50' : ''}`}>
                      <Trophy className="h-4 w-4 text-amber-600 dark:text-amber-300" />
                      <span className="text-sm text-amber-800 dark:text-amber-200">Records</span>
                    </Link>
                    <Link to="/faq"
                      onClick={() => console.log('🔗 Navigation click: FAQ')}
                      className={`transition-colors ${location.pathname === '/faq' ? 'text-primary font-medium' : 'text-foreground hover:text-primary'}`}>FAQ</Link>
                    <Link to="/contact" 
                      onClick={() => console.log('🔗 Navigation click: Contact')}
                      className={`transition-colors ${location.pathname === '/contact' ? 'text-primary font-medium' : 'text-foreground hover:text-primary'}`}>Contact</Link>
                  </nav>
                  <div className="flex items-center space-x-2 ml-6">
                    <button
                      type="button"
                      className="md:hidden p-2 rounded-md text-foreground hover:bg-muted/50"
                      onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                      aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                      aria-expanded={isMobileMenuOpen}
                    >
                      {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Mobile Menu */}
              <AnimatePresence>
                {isMobileMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.15 }}
                    className="md:hidden bg-background/70 border-b border-border mt-4"
                  >
                    <nav className="flex flex-col space-y-2 p-4 pt-2">
                      <Link 
                        to="/" 
                        className={`px-4 py-2 rounded-md ${location.pathname === '/' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          if (currentPage !== 1) {
                            handlePageChange(1);
                          }
                        }}
                      >
                        Home
                      </Link>
                      <Link 
                        to="/bookmarks" 
                        className={`px-4 py-2 rounded-md ${location.pathname === '/bookmarks' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Bookmarks
                      </Link>
                      <Link 
                        to="/submit" 
                        className={`px-4 py-2 rounded-md ${location.pathname === '/submit' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Submit View
                      </Link>
                      <Link
                        to="/records"
                        className={`flex items-center gap-2 px-4 py-2 border rounded-md transition-all font-medium bg-gradient-to-r from-amber-100/80 to-yellow-200/50 dark:from-amber-900/30 dark:to-yellow-800/40 border-amber-300/70 dark:border-amber-600/50 ${location.pathname === '/records' ? 'ring-2 ring-amber-400/50' : ''}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Trophy className="h-4 w-4 text-amber-600 dark:text-amber-300" />
                        <span className="text-amber-800 dark:text-amber-200">Records</span>
                      </Link>
                      <Link
                        to="/faq"
                        className={`px-4 py-2 rounded-md ${location.pathname === '/faq' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        FAQ
                      </Link>
                      <Link
                        to="/contact" 
                        className={`px-4 py-2 rounded-md ${location.pathname === '/contact' ? 'bg-primary/10 text-primary' : 'hover:bg-muted/50'}`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Contact
                      </Link>
                    </nav>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </header>

          {/* Main Content */}
          {/* NAVIGATION FIX: Robust route rendering with forced re-mounts */}
          <div key={`route-${renderKey}`}>
            {renderCurrentPage()}
          </div>
          
          {/* TODO: Add other routes as needed */}
          
          {/* React Router Routes was broken - replaced with working conditional navigation above */}

          {/* View Modal - Conditional for Mobile/Desktop */}
          <AnimatePresence>
            {selectedView && (
              isMobileDevice ? (
                <ViewModalMobile
                  view={selectedView}
                  open={selectedView !== null}
                  onClose={() => {
                    // Go back in history if we pushed a modal state (closes modal via history)
                    if (window.history.state?.modalOpen) {
                      window.history.back();
                    } else {
                      setSelectedView(null);
                    }
                  }}
                  onShare={() => handleShare(selectedView)}
                  onNext={() => {
                    const currentIndex = filteredViews.findIndex(v => v.id === selectedView.id);
                    if (currentIndex < filteredViews.length - 1) {
                      setSelectedView(filteredViews[currentIndex + 1]);
                    }
                  }}
                  onPrevious={() => {
                    const currentIndex = filteredViews.findIndex(v => v.id === selectedView.id);
                    if (currentIndex > 0) {
                      setSelectedView(filteredViews[currentIndex - 1]);
                    }
                  }}
                  onUpdateNotes={updateViewNotes}
                />
              ) : (
                <ViewModal
                  view={selectedView}
                  open={selectedView !== null}
                  onClose={() => {
                    // Simply close the modal without interfering with navigation
                    setSelectedView(null);
                  }}
                  onShare={() => handleShare(selectedView)}
                  onNext={() => {
                    // Find current view index in the filtered array of views
                    const currentIndex = filteredViews.findIndex(v => v.id === selectedView.id);
                    // If there's a next view, select it
                    if (currentIndex < filteredViews.length - 1) {
                      setSelectedView(filteredViews[currentIndex + 1]);
                    }
                  }}
                  onPrevious={() => {
                    // Find current view index in the filtered array of views
                    const currentIndex = filteredViews.findIndex(v => v.id === selectedView.id);
                    // If there's a previous view, select it
                    if (currentIndex > 0) {
                      setSelectedView(filteredViews[currentIndex - 1]);
                    }
                  }}
                  onUpdateNotes={updateViewNotes}
                />
              )
            )}
          </AnimatePresence>
          
          {/* Use a simpler, non-animated version of the scroll to top button in performance mode */}
          {showScrollToTop && (
            <button
              onClick={scrollToTop}
              className="fixed bottom-4 left-4 p-3 bg-primary text-primary-foreground rounded-full shadow-lg transition-all z-50"
              aria-label="Scroll to top"
            >
              <ArrowUpCircle className="h-6 w-6" />
            </button>
          )}

          {/* Share dialog */}
          <ShareDialog
            url={shareUrl}
            view={shareView}
            open={isShareDialogOpen}
            onClose={() => setIsShareDialogOpen(false)}
          />

          {/* Display toast messages */}
          <ToastViewport />
          
          {/* Analytics */}
          <Analytics />

          {/* Latest Confirmed Widget - Responsive: floating when wide, popout when narrow */}
          {!isLoading && !error && views.length > 0 && (
            <ResponsiveLatestConfirmedWidget
                views={views}
                onViewClick={setSelectedView}
                onShare={handleShare}
              className="absolute top-[160px] left-[calc(50vw-600px-290px)] z-50 opacity-90 scale-[0.85] w-64"
              />
          )}

          {/* Subreddit Widget - Responsive: floating when wide, popout when narrow */}
          <ResponsiveSubredditWidget
            onShare={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'Check out r/FromAfar',
                  text: 'Exploring distant views and remote perspectives together',
                  url: 'https://reddit.com/r/FromAfar',
                });
              } else {
                navigator.clipboard.writeText('https://reddit.com/r/FromAfar');
              }
            }}
            className="absolute top-[160px] right-[calc(50vw-600px-290px)] z-50 opacity-90 scale-[0.85] w-64"
          />
        </div>

        {/* Footer - Make sure this is outside the Routes component */}
        <Footer />
      </div>
    </ToastProvider>
  );
}

// ViewsLayout Component with proper type annotation
function ViewsLayout({ 
  views, 
  paginatedGroupedViews, 
  isLoading, 
  error, 
  viewMode, 
  setViewMode, 
  isCompactMode, 
  setIsCompactMode, 
  handleFilter, 
  setSelectedView, 
  toggleBookmark, 
  isBookmarked, 
  handleShare, 
  viewTransition, 
  setViewTransition,
  currentPage,
  totalPages,
  perPage,
  totalItems,
  onPageChange,
  onPerPageChange,
  filters,
  setFilters,
  isFilterApplied,
  onRandomView
}: ViewsLayoutProps & {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Analytics />
      
      {/* Temporary Debug Component - Disabled to prevent errors */}
      {/* {!isLoading && !error && views.length > 0 && <ViewDebugInfo views={views} />} */}
      
      {/* FilterBar Component - Restored to original layout */}
      <div className="mb-8">
        <FilterBar 
          onFilter={handleFilter} 
          views={views} 
          isLoading={isLoading}
          initialFilters={filters}
          onRandomView={onRandomView}
        />
        
        <div className="mt-4 flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center space-x-2">
            {/* View mode toggles */}
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md border ${viewMode === 'grid' ? 'bg-primary/20 text-primary border-primary' : 'bg-background border-border'} transition-colors`}
              aria-label="Grid view"
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md border ${viewMode === 'list' ? 'bg-primary/20 text-primary border-primary' : 'bg-background border-border'} transition-colors`}
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </button>
            {/* <button
              type="button"
              onClick={() => setViewMode('map')}
              className={`p-2 rounded-md border ${viewMode === 'map' ? 'bg-primary/20 text-primary border-primary' : 'bg-background border-border'} transition-colors`}
              aria-label="Map view"
            >
              <MapIcon className="h-4 w-4" />
            </button> */}
            
            {/* Active Filters Display */}
            <div className="flex flex-wrap gap-1.5 ml-3">
              {/* Status filters are now filtered out from this display */}
              
              {filters.sourceStates.length > 0 && filters.sourceStates.map(state => (
                <div key={`source-${state}`} className="inline-flex items-center gap-1 bg-blue-500/10 text-blue-500 px-2.5 py-1 rounded-full text-xs font-medium">
                  <span>Source: {state}</span>
                  <button 
                    type="button"
                    onClick={() => {
                      const newFilters = {...filters, sourceStates: filters.sourceStates.filter(s => s !== state)};
                      handleFilter(newFilters);
                      setFilters(newFilters);
                    }}
                    className="h-4 w-4 flex items-center justify-center rounded-full hover:bg-blue-500/20"
                    aria-label={`Remove ${state} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
              
              {/* Add similar blocks for other active filters: sourceCountries, destinationStates, etc. */}
              

            </div>
          </div>
        </div>
      </div>

      {/* Top Pagination */}
      {!isLoading && !error && views.length > 0 && (
        <div className="mb-8">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </div>
      )}

      {/* Map View - Show even with no data (uses mock data) */}
      {!isLoading && !error && viewMode === 'map' && (
        <div className="mb-8">
          <InteractiveMapLeaflet
            views={views}
            onViewSelect={setSelectedView}
            height="600px"
            className="w-full"
          />
        </div>
      )}

      {/* Main Content Area - Restored to original layout */}
      {!isLoading && !error && views.length > 0 && (
        <div>
          
          {/* View Categories - only render when we have data and not in map mode */}
          {viewMode !== 'map' && paginatedGroupedViews.map(({ range, views }) => 
            views.length > 0 && (
              <div key={range} className="mb-12">
                <div className="mb-8 relative flex items-center">
                  {/* Category header content */}
                  <div className="absolute left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent top-1/2 -z-10" />
                  <div className="absolute left-0 right-0 h-[1px] blur-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent top-1/2 -z-10" />
                  
                  <div className={cn(
                    "relative inline-flex items-center px-6 py-2.5 rounded-full shadow-lg border border-primary/20",
                    "bg-gradient-to-r",
                    range === '500+km' ? 'from-indigo-600/90 to-blue-600/90 dark:from-indigo-900/90 dark:to-blue-900/90' : 
                    range === '100-500km' ? 'from-blue-600/90 to-cyan-600/90 dark:from-blue-800/90 dark:to-cyan-800/90' : 
                    'from-cyan-600/90 to-teal-600/90 dark:from-cyan-800/90 dark:to-teal-800/90'
                  )}>
                    <div className={cn(
                      "absolute inset-0 rounded-full blur-md opacity-50 -z-10",
                      range === '500+km' ? 'bg-indigo-600' : 
                      range === '100-500km' ? 'bg-blue-600' : 
                      'bg-cyan-600'
                    )} />
                    
                    <div className="absolute left-0 top-0 w-full h-full">
                      <div className="absolute w-1 h-1 rounded-full bg-white/40 left-2 top-[50%] -translate-y-1/2" />
                      <div className="absolute w-0.5 h-0.5 rounded-full bg-white/40 left-4 top-[35%]" />
                      <div className="absolute w-0.5 h-0.5 rounded-full bg-white/40 left-3 top-[65%]" />
                    </div>
                    
                    <h2 className="text-xl font-bold text-white tracking-wide">
                      {range}
                    </h2>
                  </div>
                  
                  <div className="ml-4 flex items-center">
                    <div className="w-2 h-2 rounded-full bg-primary/70" />
                    <div className="w-8 h-[1px] bg-gradient-to-r from-primary/70 to-transparent" />
                    <div className="w-1 h-1 rounded-full bg-primary/50" />
                    <div className="w-12 h-[1px] bg-gradient-to-r from-primary/50 to-transparent" />
                  </div>
                  
                  <div className="flex-1 ml-4">
                    <div className="h-[1px] w-full bg-gradient-to-r from-primary/30 to-transparent" />
                  </div>
                </div>
                
                {/* View Cards - Back to original grid layout */}
                <div className={`${viewMode === 'list' ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'}`}>
                  {views.map(view => (
                    <ViewCard
                      key={view.id}
                      view={view}
                      onClick={() => {
                        // ViewCard clicked - View selected
                        setSelectedView(view);
                      }}
                      isBookmarked={isBookmarked(view.id)}
                      onBookmarkToggle={(e) => {
                        e.stopPropagation();
                        toggleBookmark(view.id);
                      }}
                      onShare={(e) => {
                        e.stopPropagation();
                        handleShare(view);
                      }}
                      isCompact={isCompactMode}
                      isList={viewMode === 'list'}
                    />
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 animate-pulse">
          <ViewSkeleton key="skeleton-1" />
          <ViewSkeleton key="skeleton-2" />
          <ViewSkeleton key="skeleton-3" />
          <ViewSkeleton key="skeleton-4" />
          <ViewSkeleton key="skeleton-5" />
          <ViewSkeleton key="skeleton-6" />
          <ViewSkeleton key="skeleton-7" />
          <ViewSkeleton key="skeleton-8" />
        </div>
      )}

      {/* Error State */}
      {!isLoading && error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 p-4 rounded-md mb-8">
          <p>{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && views.length === 0 && (
        <div className="text-center py-20">
          <Mountain className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">No views found</h2>
          <p className="text-muted-foreground">
            Try adjusting your filters to see more results
          </p>
        </div>
      )}

      {/* Bottom Pagination */}
      {!isLoading && !error && views.length > 0 && (
        <div className="mt-8">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </div>
      )}
    </main>
  );
}

// BookmarksLayout Component with proper type annotation
function BookmarksLayout({ 
  bookmarkedViews, 
  paginatedGroupedViews, 
  isLoading, 
  error, 
  viewMode, 
  setViewMode, 
  isCompactMode, 
  setIsCompactMode, 
  handleFilter, 
  setSelectedView, 
  toggleBookmark, 
  isBookmarked, 
  handleShare, 
  exportData, 
  viewTransition, 
  setViewTransition,
  currentPage,
  totalPages,
  perPage,
  totalItems,
  onPageChange,
  onPerPageChange,
  filters,
  setFilters
}: BookmarksLayoutProps & {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Add random bookmark view handler
  const handleRandomBookmarkView = () => {
    if (bookmarkedViews.length > 0) {
      const randomIndex = Math.floor(Math.random() * bookmarkedViews.length);
      const randomView = bookmarkedViews[randomIndex];
      setSelectedView(randomView);
    }
  };
  
  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold gradient-text">Your Bookmarks</h1>
          <p className="text-muted-foreground mt-2">
            {bookmarkedViews.length} saved views
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={exportData}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export Bookmarks</span>
          </button>
          
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md border ${viewMode === 'grid' ? 'bg-primary/20 text-primary border-primary' : 'bg-background border-border'} transition-colors`}
              aria-label="Grid view"
            >
              <Grid className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md border ${viewMode === 'list' ? 'bg-primary/20 text-primary border-primary' : 'bg-background border-border'} transition-colors`}
              aria-label="List view"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      
      {/* FilterBar Component */}
      <div className="mb-8">
        <FilterBar 
          onFilter={handleFilter} 
          views={bookmarkedViews} 
          isLoading={isLoading} 
          initialFilters={filters}
          onRandomView={handleRandomBookmarkView}
        />
      </div>

      {/* Top Pagination */}
      {!isLoading && bookmarkedViews.length > 0 && (
        <div className="mb-8">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </div>
      )}

      {/* Empty State */}
      {bookmarkedViews.length === 0 && (
        <div className="text-center py-20 bg-card border border-border rounded-lg">
          <Bookmark className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">No bookmarks yet</h2>
          <p className="text-muted-foreground mb-6">
            Save your favorite views by clicking the bookmark icon
          </p>
          <Link 
            to="/" 
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Browse Views
          </Link>
        </div>
      )}

      {/* View Grid/List */}
      {!isLoading && !error && (
        <div>
          {paginatedGroupedViews.map(({ range, views }) => 
            views.length > 0 && (
              <div key={range} className="mb-12">
                <div className="mb-8 relative flex items-center">
                  {/* Futuristic horizontal line with glow effect */}
                  <div className="absolute left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/50 to-transparent top-1/2 -z-10" />
                  <div className="absolute left-0 right-0 h-[1px] blur-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent top-1/2 -z-10" />
                  
                  {/* Pill shape with gradient background */}
                  <div className={cn(
                    "relative inline-flex items-center px-6 py-2.5 rounded-full shadow-lg border border-primary/20",
                    "bg-gradient-to-r",
                    range === '500+km' ? 'from-indigo-600/90 to-blue-600/90 dark:from-indigo-900/90 dark:to-blue-900/90' : 
                    range === '100-500km' ? 'from-blue-600/90 to-cyan-600/90 dark:from-blue-800/90 dark:to-cyan-800/90' : 
                    'from-cyan-600/90 to-teal-600/90 dark:from-cyan-800/90 dark:to-teal-800/90'
                  )}>
                    {/* Subtle glow effect */}
                    <div className={cn(
                      "absolute inset-0 rounded-full blur-md opacity-50 -z-10",
                      range === '500+km' ? 'bg-indigo-600' : 
                      range === '100-500km' ? 'bg-blue-600' : 
                      'bg-cyan-600'
                    )} />
                    
                    {/* Small decorative dots */}
                    <div className="absolute left-0 top-0 w-full h-full">
                      <div className="absolute w-1 h-1 rounded-full bg-white/40 left-2 top-[50%] -translate-y-1/2" />
                      <div className="absolute w-0.5 h-0.5 rounded-full bg-white/40 left-4 top-[35%]" />
                      <div className="absolute w-0.5 h-0.5 rounded-full bg-white/40 left-3 top-[65%]" />
                    </div>
                    
                    {/* Range text with futuristic styling */}
                    <h2 className="text-xl font-bold text-white tracking-wide">
                      {range}
                    </h2>
                  </div>
                  
                  {/* Decorative elements after the pill */}
                  <div className="ml-4 flex items-center">
                    <div className="w-2 h-2 rounded-full bg-primary/70" />
                    <div className="w-8 h-[1px] bg-gradient-to-r from-primary/70 to-transparent" />
                    <div className="w-1 h-1 rounded-full bg-primary/50" />
                    <div className="w-12 h-[1px] bg-gradient-to-r from-primary/50 to-transparent" />
                  </div>
                  
                  {/* Decorative ending elements */}
                  <div className="flex-1 ml-4">
                    <div className="h-[1px] w-full bg-gradient-to-r from-primary/30 to-transparent" />
                  </div>
                </div>
                
                <div className={`${viewMode === 'list' ? 'space-y-4' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'}`}>
                  {views.map(view => (
                    <ViewCard
                      key={view.id}
                      view={view}
                      onClick={() => {
                        // ViewCard clicked - View selected
                        setSelectedView(view);
                      }}
                      isBookmarked={isBookmarked(view.id)}
                      onBookmarkToggle={(e) => {
                        e.stopPropagation();
                        toggleBookmark(view.id);
                      }}
                      onShare={(e) => {
                        e.stopPropagation();
                        handleShare(view);
                      }}
                      isCompact={isCompactMode}
                      isList={viewMode === 'list'}
                    />
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* Bottom Pagination */}
      {!isLoading && bookmarkedViews.length > 0 && (
        <div className="mt-8">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            perPage={perPage}
            totalItems={totalItems}
            onPageChange={onPageChange}
            onPerPageChange={onPerPageChange}
          />
        </div>
      )}
      
    </main>
  );
}

export default App;